import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import splitArray from '../../../../../utils/splitArray';
import { MappedActivityRating } from '../../types';
import { Value } from '../../../types/CalendarTypes';

interface UseSearchesInterface {
  fullRatings: MappedActivityRating[];
  setRatingsSplited: Dispatch<SetStateAction<Array<MappedActivityRating[]>>>;
  setFilteredRatings: Dispatch<SetStateAction<MappedActivityRating[]>>;
}

export default function useSearches({
  fullRatings,
  setRatingsSplited,
  setFilteredRatings,
}: UseSearchesInterface) {
  const [selectedActivity, setSelectedActivity] = useState({ value: '', label: 'Filtrar por atividade' });
  const [selectedTeacherRate, setSelectedTeacherRate] = useState({ value: '', label: 'Todas' });
  const [selectedContentRate, setSelectedContentRate] = useState({ value: '', label: 'Todas' });
  const [selectedDurationRate, setSelectedDurationRate] = useState({ value: '', label: 'Todas' });
  const [selectedPeriod, setSelectedPeriod] = useState<Value>(null);

  const filterRatings = useCallback(() => {
    const ratingsFiltered = fullRatings.filter((rating) => {
      const activityFilter = rating.activity === selectedActivity.value || selectedActivity.value === '';
      const teacherRateFilter = rating.teacherRate.toString() === selectedTeacherRate.value || selectedTeacherRate.value === '';
      const contentRateFilter = rating.contentRate.toString() === selectedContentRate.value || selectedContentRate.value === '';
      const durationRateFilter = rating.durationRate === selectedDurationRate.value || selectedDurationRate.value === '';

      // Filtro por período
      let periodFilter = true;
      if (selectedPeriod && Array.isArray(selectedPeriod) && selectedPeriod[0] && selectedPeriod[1]) {
        const ratingDate = new Date(rating.ratedAt);
        const startDate = startOfDay(selectedPeriod[0]);
        const endDate = endOfDay(selectedPeriod[1]);
        periodFilter = isWithinInterval(ratingDate, { start: startDate, end: endDate });
      }

      return activityFilter && teacherRateFilter && contentRateFilter && durationRateFilter && periodFilter;
    });

    const ratingsSplitedByFilters = splitArray(ratingsFiltered);
    setRatingsSplited(ratingsSplitedByFilters);
    setFilteredRatings(ratingsSplitedByFilters[0]);
  }, [
    fullRatings,
    selectedActivity.value,
    selectedTeacherRate.value,
    selectedContentRate.value,
    selectedDurationRate.value,
    selectedPeriod,
    setFilteredRatings,
    setRatingsSplited,
  ]);

  useEffect(() => {
    filterRatings();
  }, [filterRatings]);

  const handleActivityChange = useCallback((event: { value: string; label: string }) => {
    setSelectedActivity(event);
  }, []);

  const handleTeacherRateChange = useCallback((event: { value: string; label: string }) => {
    setSelectedTeacherRate(event);
  }, []);

  const handleContentRateChange = useCallback((event: { value: string; label: string }) => {
    setSelectedContentRate(event);
  }, []);

  const handleDurationRateChange = useCallback((event: { value: string; label: string }) => {
    setSelectedDurationRate(event);
  }, []);

  const handlePeriodChange = useCallback((value: Value) => {
    setSelectedPeriod(value);
  }, []);

  const handleClearPeriod = useCallback(() => {
    setSelectedPeriod(null);
  }, []);

  return {
    selectedActivity,
    selectedTeacherRate,
    selectedContentRate,
    selectedDurationRate,
    selectedPeriod,
    handleActivityChange,
    handleTeacherRateChange,
    handleContentRateChange,
    handleDurationRateChange,
    handlePeriodChange,
    handleClearPeriod,
  };
}
